import { memo, type CSSProperties } from "react";
import { useSortable, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDroppable } from "@dnd-kit/core";
import type { DocNode, EmailDocument, RichDoc } from "@edm/schema";
import { api } from "../../api/client";
import { useEditorStore } from "../store/editorStore";
import { removeNode, duplicateNode } from "../docops";
import { effectiveStyles, toCss } from "./effectiveStyles";
import { RichTextEditor } from "../richtext/RichTextEditor";

interface NodeProps {
    node: DocNode;
    parentId: string;
    document: EmailDocument;
}

export const NodeRenderer = memo(function NodeRenderer({ node, parentId, document: doc }: NodeProps) {
    const selectedId = useEditorStore((s) => s.selectedId);
    const select = useEditorStore((s) => s.select);
    const applyDocChange = useEditorStore((s) => s.applyDocChange);
    const selected = selectedId === node.id;

    const dropSide = useEditorStore((s) =>
        s.dropHint && s.dropHint.nodeId === node.id ? s.dropHint.side : null,
    );

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({
            id: node.id,
            data: { kind: "node", nodeId: node.id, parentId, nodeType: node.type },
            disabled: node.locked === true,
        });

    const wrapStyle: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : undefined,
    };

    const onSelect = (e: React.MouseEvent) => {
        e.stopPropagation();
        select(node.id);
    };

    return (
        <div
            ref={setNodeRef}
            style={wrapStyle}
            className={`node node-${node.type}${selected ? " selected" : ""}${
                dropSide ? ` drop-${dropSide}` : ""
            }`}
            data-node-id={node.id}
            data-testid={`node-${node.type}`}
            onClick={onSelect}
        >
            {selected && node.locked && (
                <div className="block-toolbar locked" onClick={(e) => e.stopPropagation()}>
                    <span className="block-toolbar-label">🔒 {node.type} (locked)</span>
                </div>
            )}
            {selected && !node.locked && (
                <div className="block-toolbar" onClick={(e) => e.stopPropagation()}>
                    <span className="block-toolbar-label">{node.type}</span>
                    <button
                        type="button"
                        className="block-btn drag-handle"
                        title="Drag to move"
                        aria-label="Drag to move"
                        {...attributes}
                        {...listeners}
                    >
                        ⠿
                    </button>
                    <button
                        type="button"
                        className="block-btn"
                        title="Duplicate"
                        aria-label="Duplicate block"
                        onClick={() => applyDocChange((d) => void duplicateNode(d, node.id))}
                    >
                        ⧉
                    </button>
                    <button
                        type="button"
                        className="block-btn"
                        title="Save as reusable block"
                        aria-label="Save as reusable block"
                        data-testid="save-as-block"
                        onClick={async () => {
                            const name = window.prompt("Save block as:", node.type);
                            if (!name) return;
                            await api.post("/api/v1/blocks", { name, scope: "tenant", block: node });
                            useEditorStore.getState().bumpSavedBlocks();
                        }}
                    >
                        ★
                    </button>
                    <button
                        type="button"
                        className="block-btn danger"
                        title="Delete"
                        aria-label="Delete block"
                        onClick={() => {
                            applyDocChange((d) => void removeNode(d, node.id));
                            select(null);
                        }}
                    >
                        ✕
                    </button>
                </div>
            )}
            <BlockView node={node} document={doc} />
        </div>
    );
});

function BlockView({ node, document: doc }: { node: DocNode; document: EmailDocument }) {
    const applyDocChange = useEditorStore((s) => s.applyDocChange);
    const styles = effectiveStyles(doc, node);
    const fontFallback = doc.settings.fontFamilies.join(", ");
    const css = toCss(styles, fontFallback);

    const commitRichText = (content: RichDoc) =>
        applyDocChange(
            (d) => {
                const n = findInDraft(d.root, node.id);
                if (n) n.attrs.content = content;
            },
            { coalesceKey: `text:${node.id}`, fromRichText: true },
        );

    switch (node.type) {
        case "section":
            return (
                <div className="section-view" style={css}>
                    {(node.children ?? []).map((row) => (
                        <NodeRenderer key={row.id} node={row} parentId={node.id} document={doc} />
                    ))}
                </div>
            );

        case "row": {
            const ratios = (node.attrs.columnRatios as number[] | undefined) ?? [1];
            const total = ratios.reduce((a, b) => a + b, 0);
            return (
                <div className="row-view" style={css}>
                    {(node.children ?? []).map((col, i) => (
                        <ColumnView
                            key={col.id}
                            column={col}
                            widthPercent={((ratios[i] ?? 1) / total) * 100}
                            document={doc}
                        />
                    ))}
                </div>
            );
        }

        case "heading": {
            const level = (node.attrs.level as number | undefined) ?? 2;
            return (
                <div className={`heading-view h${level}`} style={css}>
                    <RichTextEditor
                        nodeId={node.id}
                        value={node.attrs.content as RichDoc}
                        singleParagraph
                        onCommit={commitRichText}
                    />
                </div>
            );
        }

        case "paragraph":
            return (
                <div className="paragraph-view" style={css}>
                    <RichTextEditor
                        nodeId={node.id}
                        value={node.attrs.content as RichDoc}
                        onCommit={commitRichText}
                    />
                </div>
            );

        case "image": {
            const src = (node.attrs.src as string) || "";
            const align = styles.align ?? "center";
            return (
                <div className="image-view" style={{ ...css, textAlign: align }}>
                    {src ? (
                        <img
                            src={src}
                            alt={(node.attrs.alt as string) ?? ""}
                            style={{
                                width: styles.width ?? "100%",
                                maxWidth: "100%",
                                borderRadius: styles.borderRadius,
                            }}
                        />
                    ) : (
                        <div className="image-placeholder">Select an image in the inspector</div>
                    )}
                </div>
            );
        }

        case "button": {
            const align = styles.align ?? "center";
            return (
                <div className="button-view" style={{ ...css, textAlign: align, backgroundColor: undefined }}>
                    <span
                        className="button-pill"
                        style={{
                            backgroundColor: styles.backgroundColor,
                            color: styles.color,
                            fontSize: styles.fontSize,
                            fontWeight: styles.fontWeight,
                            fontFamily: css.fontFamily,
                            borderRadius: styles.borderRadius,
                        }}
                    >
                        {(node.attrs.text as string) || "Button"}
                    </span>
                </div>
            );
        }

        case "divider":
            return (
                <div className="divider-view" style={css}>
                    <div
                        style={{
                            borderTop: `${styles.borderWidth ?? 1}px ${styles.borderStyle ?? "solid"} ${styles.borderColor ?? "#e2e5ea"}`,
                        }}
                    />
                </div>
            );

        case "spacer":
            return (
                <div
                    className="spacer-view"
                    style={{ height: styles.height ?? 24 }}
                    title={`Spacer ${styles.height ?? 24}px`}
                />
            );

        case "hero": {
            const src = (node.attrs.src as string | undefined) ?? "";
            return (
                <div
                    className="hero-view"
                    style={{
                        ...css,
                        backgroundImage: src ? `url(${src})` : undefined,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                >
                    {(node.children ?? []).map((child) => (
                        <NodeRenderer key={child.id} node={child} parentId={node.id} document={doc} />
                    ))}
                    {(node.children ?? []).length === 0 && (
                        <div className="column-empty">Drop hero content here</div>
                    )}
                </div>
            );
        }

        case "card":
            return (
                <div
                    className="card-view"
                    style={{
                        ...css,
                        border: `${styles.borderWidth ?? 1}px ${styles.borderStyle ?? "solid"} ${styles.borderColor ?? "#e2e5ea"}`,
                    }}
                >
                    {(node.children ?? []).map((child) => (
                        <NodeRenderer key={child.id} node={child} parentId={node.id} document={doc} />
                    ))}
                    {(node.children ?? []).length === 0 && (
                        <div className="column-empty">Drop card content here</div>
                    )}
                </div>
            );

        case "logo": {
            const src = (node.attrs.src as string) || "";
            return (
                <div className="logo-view" style={{ ...css, textAlign: styles.align ?? "center" }}>
                    {src ? (
                        <img src={src} alt={(node.attrs.alt as string) ?? "Logo"} style={{ width: styles.width ?? 160, maxWidth: "100%" }} />
                    ) : (
                        <div className="image-placeholder">Set a logo URL in the inspector</div>
                    )}
                </div>
            );
        }

        case "social": {
            const links = (node.attrs.links as { network: string; url: string }[]) ?? [];
            const size = (node.attrs.iconSize as number) ?? 28;
            return (
                <div className="social-view" style={{ ...css, textAlign: styles.align ?? "center" }}>
                    {links.map((l, i) => (
                        <span
                            key={i}
                            className="social-chip"
                            style={{ width: size, height: size, lineHeight: `${size}px` }}
                            title={`${l.network}: ${l.url || "no url"}`}
                        >
                            {l.network.slice(0, 2)}
                        </span>
                    ))}
                    {links.length === 0 && <span className="column-empty">Add social links</span>}
                </div>
            );
        }

        case "nav": {
            const links = (node.attrs.links as { label: string; href: string }[]) ?? [];
            return (
                <div className="nav-view" style={css}>
                    {links.map((l, i) => (
                        <span key={i} className="nav-link">
                            {l.label}
                        </span>
                    ))}
                </div>
            );
        }

        case "callout":
            return (
                <div className="callout-outer" style={{ ...css, backgroundColor: undefined }}>
                    <div
                        className="callout-inner"
                        style={{
                            backgroundColor: styles.backgroundColor,
                            borderRadius: styles.borderRadius,
                            padding: 16,
                        }}
                    >
                        <RichTextEditor
                            nodeId={node.id}
                            value={node.attrs.content as RichDoc}
                            onCommit={commitRichText}
                        />
                    </div>
                </div>
            );

        case "quote":
            return (
                <div className="quote-view" style={css}>
                    <div
                        style={{
                            borderLeft: `4px solid ${styles.borderColor ?? "#2f6fed"}`,
                            paddingLeft: 16,
                            fontStyle: "italic",
                        }}
                    >
                        <RichTextEditor
                            nodeId={node.id}
                            value={node.attrs.content as RichDoc}
                            onCommit={commitRichText}
                        />
                        {(node.attrs.attribution as string) && (
                            <p className="quote-attribution">— {node.attrs.attribution as string}</p>
                        )}
                    </div>
                </div>
            );

        case "footer":
            return (
                <div className="footer-view" style={css}>
                    <RichTextEditor
                        nodeId={node.id}
                        value={node.attrs.content as RichDoc}
                        onCommit={commitRichText}
                    />
                    {node.attrs.includeUnsubscribe !== false && (
                        <p className="footer-unsub">
                            <u>{(node.attrs.unsubscribeLabel as string) || "Unsubscribe"}</u>{" "}
                            <span className="merge-tag-chip">{"{{unsubscribe_url}}"}</span>
                        </p>
                    )}
                </div>
            );

        case "rawHtml":
            return (
                <div className="rawhtml-view">
                    <div className="rawhtml-badge">HTML</div>
                    <iframe
                        title="Raw HTML preview"
                        sandbox=""
                        srcDoc={(node.attrs.html as string) ?? ""}
                        className="rawhtml-frame"
                    />
                </div>
            );

        default:
            return <div className="unknown-view">Unsupported block: {node.type}</div>;
    }
}

function ColumnView({
    column,
    widthPercent,
    document: doc,
}: {
    column: DocNode;
    widthPercent: number;
    document: EmailDocument;
}) {
    const { setNodeRef, isOver } = useDroppable({
        id: `container:${column.id}`,
        data: { kind: "container", containerId: column.id },
    });
    const children = column.children ?? [];
    return (
        <div
            ref={setNodeRef}
            className={`column-view${isOver ? " over" : ""}${children.length === 0 ? " empty" : ""}`}
            style={{ width: `${widthPercent}%` }}
            data-node-id={column.id}
        >
            <SortableContext
                items={children.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
            >
                {children.map((child) => (
                    <NodeRenderer key={child.id} node={child} parentId={column.id} document={doc} />
                ))}
            </SortableContext>
            {children.length === 0 && <div className="column-empty">Drop content here</div>}
        </div>
    );
}

function findInDraft(root: DocNode, id: string): DocNode | null {
    if (root.id === id) return root;
    for (const child of root.children ?? []) {
        const hit = findInDraft(child, id);
        if (hit) return hit;
    }
    return null;
}
